import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Globe, Check, ChevronDown } from "lucide-react";
import { LANGUAGES } from "@/i18n";

export default function LanguageSwitcher({ navigateOnChange = true }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const currentCode = i18n.resolvedLanguage || i18n.language || "en";
  const current = LANGUAGES.find((l) => l.code === currentCode) || LANGUAGES[0];

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
    if (navigateOnChange) navigate(`/${code}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="language-switcher"
        aria-label="Change language"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full border hairline px-3 py-2 text-sm text-neutral-300 hover:border-white/30 hover:text-white transition-colors duration-300"
      >
        <Globe size={15} />
        <span className="uppercase">{current.code}</span>
        <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="language-menu"
          className="absolute end-0 mt-2 w-44 max-h-80 overflow-auto rounded-xl border hairline bg-[#0B0B0D] p-1.5 z-[60] shadow-2xl"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              data-testid={`lang-${l.code}`}
              onClick={() => choose(l.code)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                l.code === current.code ? "text-white bg-white/5" : "text-neutral-400 hover:bg-white/5 hover:text-white"
              }`}
            >
              <span>{l.native}</span>
              {l.code === current.code && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
